import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const BASE = "/play";
const SHOTS = [
  ["screen-1-ask.png", "Ask three AIs at once"],
  ["screen-2-debate.png", "Watch them debate"],
  ["screen-3-final-answer.png", "One agreed answer"],
  ["screen-4-safety-intro.png", "Safety intro for kids"],
  ["screen-5-safety-promise.png", "The AI's safety promise"],
  ["screen-6-kids-mode.png", "Kids Mode answers"],
];

const TextBlock = ({ file, title, limit }) => {
  const [text, setText] = useState("");
  useEffect(() => { fetch(`${BASE}/${file}`).then((r) => r.text()).then((t) => setText(t.trim())); }, [file]);
  const copy = () => { navigator.clipboard.writeText(text); toast.success(`${title} copied`); };
  return (
    <section data-testid={`listing-${file.replace(".txt", "")}`} className="what-tester-card">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg font-bold text-white">{title}</h2>
        <span data-testid={`listing-${file.replace(".txt", "")}-count`} className="text-xs font-mono text-slate-500">{text.length} / {limit} characters</span>
        <div className="ml-auto flex gap-2">
          <button data-testid={`listing-${file.replace(".txt", "")}-copy`} onClick={copy} className="what-btn-ghost flex items-center gap-1.5"><Copy size={13} /> Copy</button>
          <a data-testid={`listing-${file.replace(".txt", "")}-download`} href={`${BASE}/${file}`} download className="what-btn-ghost flex items-center gap-1.5"><Download size={13} /> .txt</a>
        </div>
      </div>
      <pre className="mt-4 whitespace-pre-wrap text-sm text-slate-300 font-['Plus_Jakarta_Sans',sans-serif] max-h-80 overflow-y-auto">{text}</pre>
    </section>
  );
};

export default function PlayListingPage() {
  const { user } = useAuth();
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return (
    <div data-testid="play-listing-page" className="min-h-screen bg-[#0B0F17] text-slate-300">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-14 space-y-8">
        <Link to="/" data-testid="play-listing-back" className="text-xs font-mono text-slate-500 hover:text-white flex items-center gap-1.5"><ArrowLeft size={13} /> WHAT?</Link>
        <div>
          <h1 className="font-display text-4xl font-extrabold text-white">Google Play listing</h1>
          <p className="text-sm text-slate-400 mt-2">Everything for Play Console → Grow → Store presence → Main store listing.</p>
          <a data-testid="play-listing-download-all" href={`${BASE}/what-play-listing.zip`} download className="what-btn-primary inline-flex items-center gap-2 mt-5"><Download size={15} /> Download everything (.zip)</a>
        </div>
        <TextBlock file="short-description.txt" title="Short description" limit={80} />
        <TextBlock file="full-description.txt" title="Full description" limit={4000} />
        <section data-testid="listing-feature-graphic" className="what-tester-card">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-lg font-bold text-white">Feature graphic</h2>
            <span className="text-xs font-mono text-slate-500">1024 × 500 PNG</span>
            <a data-testid="listing-feature-graphic-download" href={`${BASE}/feature-graphic.png`} download className="ml-auto what-btn-ghost flex items-center gap-1.5"><Download size={13} /> Download</a>
          </div>
          <img src={`${BASE}/feature-graphic.png`} alt="WHAT? feature graphic" className="mt-4 w-full rounded-xl border border-white/[0.06]" />
        </section>
        <section data-testid="listing-screenshots" className="what-tester-card">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-lg font-bold text-white">Phone screenshots</h2>
            <span className="text-xs font-mono text-slate-500">1080 × 1920 PNG · {SHOTS.length} images</span>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-5">
            {SHOTS.map(([f, label], i) => (
              <a key={f} data-testid={`listing-screenshot-${i + 1}`} href={`${BASE}/${f}`} download className="group block">
                <img src={`${BASE}/${f}`} alt={label} loading="lazy" className="w-full aspect-[9/16] object-cover rounded-xl border border-white/[0.06] transition-transform group-hover:-translate-y-1" />
                <span className="mt-2 flex items-center gap-1.5 text-xs text-slate-400"><Download size={12} /> {label}</span>
              </a>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
